/**
 * `IAppCacheModel` — port of Unity's `IAppCache`, the seam behind app
 * settings' cache size readout and "Clear Cache" action
 * (`AppSettingsViewModel.OnNavigatedTo` / `ClearCache`).
 *
 * **What "cache" means differs by platform, deliberately.** Unity caches
 * project data as files under `Application.persistentDataPath` and reports the
 * directory size. A browser has no such directory: the web port's equivalent
 * local state is the module-scoped blob caches (thumbnails, glTF assets,
 * asset previews) plus the `localStorage` preference keys that stand in for
 * Unity's `PlayerPrefs`. So the web reports the browser's own storage estimate
 * and clears the caches it actually owns.
 *
 * The user-facing promise in Unity's dialog copy still holds exactly: clearing
 * erases locally-stored project info and does not affect projects.
 */

export interface CacheClearResult {
  /** Thumbnail blob URLs revoked. */
  readonly thumbnails: number;
  /** glTF asset entries evicted. */
  readonly assets: number;
  /** `localStorage` preference keys removed. */
  readonly preferences: number;
}

export interface IAppCacheModel {
  /**
   * Bytes of local storage this origin is using, or `null` when the browser
   * won't say. Mirrors `IAppCache.GetCacheSizeAsync()`.
   *
   * Backed by `navigator.storage.estimate().usage`, which covers IndexedDB /
   * Cache Storage / localStorage for the origin. It is an ESTIMATE and is
   * deliberately padded by browsers to resist cross-origin size probing, so
   * treat it as an order-of-magnitude readout — the same way Unity's directory
   * walk is a snapshot that drifts the moment anything downloads.
   */
  getCacheSizeAsync(): Promise<number | null>;

  /**
   * Drop every cache the app owns. Mirrors `IAppCache.ClearCacheAsync()` plus
   * the `ReleaseNotesExtensions.ClearReleaseNotesPrefs()` call Unity chains
   * onto it.
   *
   * Preference keys are NOT all cleared: sign-in state and the AI/session
   * preferences that survive a reload are left alone, matching Unity's
   * split between the project cache and `PlayerPrefs` it doesn't touch. See
   * the implementation for the exact key list.
   */
  clearCacheAsync(): Promise<CacheClearResult>;
}

export const APP_CACHE_MODEL = Symbol.for("Campfire.AppCacheModel");
